import { PageActionConfiguration } from "./pages/page-action-configuration";
import { PageBulkImport } from "./pages/page-bulk-import";
import { PageChecklistSettings } from "./pages/page-checklist-settings";
import { PageColumnVisibility } from "./pages/page-column-visibility";
import { PageFleetStatistics } from "./pages/page-fleet-statistics";
import { PageOldDashboard } from "./pages/page-dashboard";
import { PageDispatcher } from "@/pages/page-dispatcher";
import { PageEmailLoginTokenLogin } from "./pages/page-email-token-login";
import { PageGeneralSettings } from "./pages/page-general-settings";
import { PageImpactManagement } from "./pages/page-impact-management";
import { PageLoginReport } from "./pages/page-login-report";
import { PageMaintenance } from "./pages/page-maintenance";
import { PageMotionSensors } from "./pages/page-motion-sensors";
import { PagePedestrianActivity } from "./pages/page-pedestrian-activity";
import { PagePhones } from "./pages/page-phones";
import { PagePreOpsChecklists } from "./pages/page-pre-ops-checklists";
import { PageSafetyMessages } from "./pages/page-safety-messages";
import { PageSafetyMessagesSettings } from "./pages/page-safety-messages-settings";
import { PageSettings } from "./pages/page-settings";
import { PageShockProfiles } from "./pages/page-shock-profiles";
import { PageSystemWide } from "./pages/page-system-wide";
import { PageTruckActivity } from "./pages/page-truck-activity";
import { PageUserGroups } from "./pages/page-user-groups";
import { PageUserProductivity } from "./pages/page-user-productivity";
import { PageUserSettings } from "./pages/page-user-settings";
import { PageUsers } from "./pages/page-users";
import { PageVehicle } from "./pages/page-vehicle";
import { PageVehicleDevices } from "./pages/page-vehicle-devices";
import { PageVehicleGroups } from "./pages/page-vehicle-groups";
import { PageVehicleMovementPeriods } from "./pages/page-vehicle-movement-periods";
import { PageVehicleProductivity } from "./pages/page-vehicle-productivity";
import { PageVehicleSettings } from "./pages/page-vehicle-settings";
import { PageVehicles } from "./pages/page-vehicles";
import { PageZoningActivity } from "./pages/page-zoning-activity";
import { routePath } from "./route-path";
import {
    Bot,
    ChartSpline,
    CircleAlert,
    ClipboardList,
    Forklift,
    Home,
    KeyRound,
    MessageCircleWarning,
    Settings,
    Timer,
    TriangleAlert,
    Users,
    Wrench,
    type LucideIcon,
} from "lucide-react";
import { PageIncidents } from "@/pages/page-incidents";
import { PageDashboard } from "@/pages/page-dashboard";
import { PageLogin } from "@/pages/page-login";
import { PagePedestrianDevices } from "./pages/page-pedestrian-devices";
import { PageEloshieldClassicStationaryModules } from "./pages/page-eloshield-classic-stationary-modules";
import { PageAgv } from "@/pages/page-agv";
import { PageRobots } from "@/pages/page-robots";
import { PageStationaryUnits } from "./pages/page-stationary-units";

export interface BaseRoute<TProps> {
    /** A pattern compatible with react-router. */
    readonly pattern: string;
    /** React component that shall be rendered for this route. */
    readonly component: React.ComponentClass<TProps> | React.FC<TProps>;
    /** Whether the route is publicly visible, or only for logged in users. */
    readonly public?: boolean;
}

export interface NavigationRoute extends BaseRoute<{}> {
    /** The page's as it is supposed to show up in the navigation. */
    readonly title: string;
    /** A factory for creating a path to a specific entity. */
    readonly path: () => string;
    /** ELOfusion theme icon. */
    readonly icon?: LucideIcon;
    readonly elofleetOnly?: boolean;
}

export interface HiddenRoute<TProps> extends BaseRoute<TProps> {
    /** A factory for creating a path to a specific entity. */
    readonly path: (...args: string[]) => string;
}

export type Route<TProps> = NavigationRoute | HiddenRoute<TProps>;

/** List of all routes. */
const routes = new Set<Route<unknown>>();

/** Add a new route to the set of routes. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function declareRoute<T extends Route<any>>(route: T): T {
    routes.add(route as Route<unknown>);
    return route;
}

/**
 * Return a list of all registered routes.
 * **Make sure to use this function only when the react context is already started.**
 * Routes are added to the underlying set of routes statically, when the invocation is discovered.
 */
export function getRoutes(): Set<Route<unknown>> {
    return routes;
}

// Here's the reason for having all routes
// in one file instead of with the
// page components themselves:
// Some page components might never actually get imported anywhere,
// so their route never gets declared.
// Having all routes here means we're sure that all routes are
// declared by importing this file in our main component.

export const routePreOpsChecklistSettings = declareRoute({
    navigation: true,
    component: PageChecklistSettings,
    title: "page.checklistSettings.navbarEntry",
    path: routePath.checklistSettings,
    pattern: "/checklist-settings",
    icon: ClipboardList,
    elofleetOnly: true,
});

export const routeBulkImport = declareRoute({
    component: PageBulkImport,
    title: "page.bulkImport.navbarEntry",
    path: routePath.bulkImport,
    pattern: "/bulk-import",
    elofleetOnly: true,
});

export const routeColumnVisibility = declareRoute({
    component: PageColumnVisibility,
    title: "page.columnVisibility.navbarEntry",
    path: routePath.columnVisibility,
    pattern: "/column-visibility",
});

export const routeFleetStatistics = declareRoute({
    navigation: true,
    component: PageFleetStatistics,
    title: "page.fleetStatistics.navbarEntry",
    path: routePath.fleetStatistics,
    pattern: "/fleet-statistics",
    elofleetOnly: true,
});

/** TODO: Remove after fully migrating the dashboard to the new one. */
export const routeOldDashboard = declareRoute({
    navigation: true,
    component: PageOldDashboard,
    title: "page.dashboard.navbarEntry",
    path: routePath.oldDashboard,
    pattern: "/legacy-dashboard",
    icon: Home,
    elofleetOnly: true,
});

export const routeDashboard = declareRoute({
    navigation: true,
    component: PageDashboard,
    title: "page.dashboard.navbarEntry",
    path: routePath.dashboard,
    pattern: "/dashboard",
    icon: Home,
    elofleetOnly: true,
});

export const routeLoginEmailToken = declareRoute({
    component: PageEmailLoginTokenLogin,
    path: routePath.emailLoginTokenLogin,
    pattern: "/email-token-login",
    public: true,
});

export const routeDispatcher = declareRoute({
    public: true,
    component: PageDispatcher,
    path: routePath.dispatcher,
    pattern: "/start",
});

export const routeGeneralSettings = declareRoute({
    component: PageGeneralSettings,
    title: "page.generalSettings.navbarEntry",
    path: routePath.generalSettings,
    pattern: "/general-settings",
});

export const routeImpactManagement = declareRoute({
    navigation: true,
    component: PageImpactManagement,
    title: "page.impactManagement.navbarEntry",
    path: routePath.impactManagement,
    pattern: "/impact-management",
    icon: CircleAlert,
    elofleetOnly: true,
});

export const routeLoginReport = declareRoute({
    navigation: true,
    component: PageLoginReport,
    title: "page.loginReport.navbarEntry",
    path: routePath.loginReport,
    pattern: "/login-report",
    icon: KeyRound,
    elofleetOnly: true,
});

export const routeLogin = declareRoute({
    component: PageLogin,
    path: routePath.login,
    pattern: "/login",
    public: true,
});

export const routeMaintenance = declareRoute({
    navigation: true,
    component: PageMaintenance,
    title: "page.maintenance.navbarEntry",
    path: routePath.maintenance,
    pattern: "/maintenance",
    icon: Wrench,
    elofleetOnly: true,
});

export const routePedestrianActivity = declareRoute({
    navigation: true,
    component: PagePedestrianActivity,
    title: "page.pedestrianActivity.navbarEntry",
    path: routePath.pedestrianActivity,
    pattern: "/pedestrian-activity",
});

export const routeMotionSensors = declareRoute({
    component: PageMotionSensors,
    title: "page.motionSensors.navbarEntry",
    path: routePath.motionSensors,
    pattern: "/motion-sensors",
    elofleetOnly: true,
});

export const routePedestrianDevices = declareRoute({
    component: PagePedestrianDevices,
    title: "page.pedestrianDevices.navbarEntry",
    path: routePath.pedestrianDevices,
    pattern: "/pedestrian-devices",
});

export const routeStationaryUnits = declareRoute({
    component: PageStationaryUnits,
    title: "page.stationaryUnits.navbarEntry",
    path: routePath.stationaryUnits,
    pattern: "/stationary-units",
});

export const routeEloshieldClassicStationaryModules = declareRoute({
    component: PageEloshieldClassicStationaryModules,
    title: "page.eloshieldClassicStationaryModules.navbarEntry",
    path: routePath.eloshieldClassicStationaryModules,
    pattern: "/eloshield-classic-stationary-modules",
});

export const routePhones = declareRoute({
    component: PagePhones,
    title: "page.phones.navbarEntry",
    path: routePath.phones,
    pattern: "/phones",
    elofleetOnly: true,
});

export const routePreOpsChecklists = declareRoute({
    navigation: true,
    component: PagePreOpsChecklists,
    title: "page.preOpsChecklists.navbarEntry",
    path: routePath.preOpsChecklists,
    pattern: "/pre-ops-checklists",
    icon: ClipboardList,
    elofleetOnly: true,
});

export const routeSafetyMessagesSettings = declareRoute({
    navigation: true,
    component: PageSafetyMessagesSettings,
    title: "page.safetyMessagesSettings.navbarEntry",
    path: routePath.safetyMessagesSettings,
    pattern: "/safety-messages-settings",
    icon: MessageCircleWarning,
    elofleetOnly: true,
});

export const routeSafetyMessages = declareRoute({
    navigation: true,
    component: PageSafetyMessages,
    title: "page.safetyMessages.navbarEntry",
    path: routePath.safetyMessages,
    pattern: "/safety-messages",
    icon: MessageCircleWarning,
    elofleetOnly: true,
});

export const routeSettings = declareRoute({
    component: PageSettings,
    path: routePath.settings,
    pattern: "/settings",
    title: "page.settings.navbarEntry",
});

export const routeShockProfiles = declareRoute({
    component: PageShockProfiles,
    title: "page.shockProfiles.navbarEntry",
    path: routePath.shockProfiles,
    pattern: "/shock-profiles",
    elofleetOnly: true,
});

export const routeSystemWide = declareRoute({
    component: PageSystemWide,
    title: "page.systemWide.navbarEntry",
    path: routePath.systemWide,
    pattern: "/system-wide",
    icon: Settings,
    elofleetOnly: true,
});

export const routeTruckActivity = declareRoute({
    navigation: true,
    component: PageTruckActivity,
    title: "page.truckActivity.navbarEntry",
    path: routePath.truckActivity,
    pattern: "/truck-activity",
    elofleetOnly: true,
});

export const routeUserGroups = declareRoute({
    component: PageUserGroups,
    title: "page.userGroups.navbarEntry",
    path: routePath.userGroups,
    pattern: "/user-groups",
    elofleetOnly: true,
});

export const routeUserProductivity = declareRoute({
    navigation: true,
    component: PageUserProductivity,
    title: "page.userProductivity.navbarEntry",
    path: routePath.userProductivity,
    pattern: "/user-productivity",
    icon: ChartSpline,
    elofleetOnly: true,
});

export const routeUserSettings = declareRoute({
    navigation: true,
    component: PageUserSettings,
    title: "page.userSettings.navbarEntry",
    path: routePath.userSettings,
    pattern: "/user-settings/:tab",
    icon: Users,
});

export const routeUsers = declareRoute({
    component: PageUsers,
    title: "page.users.navbarEntry",
    path: routePath.users,
    pattern: "/users",
    icon: Users,
    elofleetOnly: true,
});

export const routeVehicleDevices = declareRoute({
    component: PageVehicleDevices,
    title: "page.vehicleDevices.navbarEntry",
    path: routePath.vehicleDevices,
    pattern: "/vehicle-devices",
});

export const routeVehicleGroups = declareRoute({
    navigation: true,
    component: PageVehicleGroups,
    title: "page.vehicleGroups.navbarEntry",
    path: routePath.vehicleGroups,
    pattern: "/vehicle-groups",
    elofleetOnly: true,
});

export const routeVehicleMovementPeriods = declareRoute({
    navigation: true,
    component: PageVehicleMovementPeriods,
    title: "page.vehicleMovementPeriods.navbarEntry",
    path: routePath.vehicleMovementPeriods,
    pattern: "/vehicle-movement-periods",
    icon: Timer,
    elofleetOnly: true,
});

export const routeVehicleProductivity = declareRoute({
    navigation: true,
    component: PageVehicleProductivity,
    title: "page.vehicleProductivity.navbarEntry",
    path: routePath.vehicleProductivity,
    pattern: "/vehicle-productivity",
    icon: ChartSpline,
    elofleetOnly: true,
});

export const routeVehicleSettings = declareRoute({
    navigation: true,
    component: PageVehicleSettings,
    title: "page.vehicleSettings.navbarEntry",
    path: routePath.vehicleSettings,
    pattern: "/vehicle-settings/:tab",
    icon: Forklift,
});

export const routeVehicles = declareRoute({
    navigation: true,
    component: PageVehicles,
    title: "page.vehicles.navbarEntry",
    path: routePath.vehicles,
    pattern: "/vehicles",
    icon: Forklift,
    elofleetOnly: true,
});

export const routeVehicle = declareRoute({
    component: PageVehicle,
    path: routePath.vehicle,
    pattern: "/vehicles/:vehicleId",
});

export const routeZoningActivity = declareRoute({
    navigation: true,
    component: PageZoningActivity,
    title: "page.zoningActivity.navbarEntry",
    path: routePath.zoningActivity,
    pattern: "/zoning-activity",
});

export const routeActionConfiguration = declareRoute({
    component: PageActionConfiguration,
    title: "page.actionConfiguration.navbarEntry",
    path: routePath.actionConfiguration,
    pattern: "/action-configuration",
});

export const routeIncidents = declareRoute({
    navigation: true,
    component: PageIncidents,
    title: "page.incidents.navbarEntry",
    path: routePath.incidents,
    pattern: "/incidents",
    icon: TriangleAlert,
    elofleetOnly: true,
});

export const routeAgv = declareRoute({
    navigation: true,
    component: PageAgv,
    title: "page.agv.navbarEntry",
    path: routePath.agv,
    pattern: "/agv",
    icon: Bot,
});

export const routeRobots = declareRoute({
    navigation: true,
    component: PageRobots,
    title: "page.robotFleet.navbarEntry",
    path: routePath.robots,
    pattern: "/robots",
    icon: Bot,
});
